'use client';

import dynamic from 'next/dynamic';
import type { ComponentProps } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRewardsHubConfig } from '@/hooks/use-rewards/use-rewards';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import NotFoundComponent from '@/app/not-found';
import { useAccountUm } from '@/hooks/use-account-um';
import { useStore } from '@/store/store';
import type { RewardsPageConfig } from '@/logic/page-config/rewards/rewards-page-config';
import {
    trackRewardsHubAmplitude,
    type RewardsHubSharedState
} from '@/tracking/amplitude/events/rewards-hub/rewards-hub';
import { REWARDS_HUB_AMP_EVENT } from '@/tracking/amplitude/events/rewards-hub/event-names';

const LoyaltyDetailsView = dynamic(
    () =>
        import('@fanduel/rewards-hub-client').then(({ LoyaltyDetailsPage, RewardsAppProvider }) => {
            const LoyaltyDetailsViewInner = (props: ComponentProps<typeof LoyaltyDetailsPage>) => (
                <RewardsAppProvider>
                    <LoyaltyDetailsPage {...props} />
                </RewardsAppProvider>
            );
            return LoyaltyDetailsViewInner;
        }),
    { ssr: false }
);

type LoyaltyDetailsClientProps = { pageConfig?: RewardsPageConfig; isLoggedIn?: boolean };

export const LoyaltyDetailsClient: React.FC<LoyaltyDetailsClientProps> = ({ pageConfig, isLoggedIn }) => {
    const sharedState = useRef<RewardsHubSharedState | null>(null);
    const [sharedStateLoaded, setSharedStateLoaded] = useState(false);
    const handleShowLoading = useStore((s) => s.loading.handleShowLoading);
    const handleHideLoading = useStore((s) => s.loading.handleHideLoading);
    const sendEvent = useStore((s) => s.tracking.sendEvent);
    const rewardsHubConfig = useRewardsHubConfig(pageConfig, isLoggedIn);
    const { triggerLogin } = useAccountUm();
    const fetchOptions = useMemo(
        () => ({
            onStart: handleShowLoading,
            onEnd: handleHideLoading
        }),
        [handleShowLoading, handleHideLoading]
    );

    const handlers = useMemo(
        () => ({
            handleSharedState: (props: RewardsHubSharedState) => {
                sharedState.current = props;
                setSharedStateLoaded((prev) => (prev ? prev : true));
            },
            handleDetailsFaqOpen: () => {
                trackRewardsHubAmplitude({
                    sendEvent,
                    event: REWARDS_HUB_AMP_EVENT.LOYALTY_DETAILS_FAQ_OPENED,
                    sharedState: sharedState.current
                });
            }
        }),
        [sendEvent]
    );

    useEffect(() => {
        if (!isLoggedIn) {
            triggerLogin({ externalReferrer: 'loyalty-details' });
        }
    }, [isLoggedIn, triggerLogin]);

    if (!isLoggedIn) {
        return <NotFoundComponent />;
    }

    return (
        <LoyaltyDetailsView
            config={rewardsHubConfig}
            isSharedStateLoaded={sharedStateLoaded}
            fetchOptions={fetchOptions}
            handlers={handlers}
        />
    );
};
